import { useEffect } from 'react'

interface DocumentMeta {
  title: string
  description: string
  iconUrl: string
  imageUrl?: string
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  )
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertIcon(rel: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
}

/**
 * Sets the browser tab title, favicon, and link-preview metadata so the
 * presentation identifies itself as Vexu wherever it is opened or shared.
 */
export function useDocumentMeta({
  title,
  description,
  iconUrl,
  imageUrl,
}: DocumentMeta) {
  useEffect(() => {
    document.title = title

    upsertIcon('icon', iconUrl)
    upsertIcon('shortcut icon', iconUrl)
    upsertIcon('apple-touch-icon', iconUrl)

    upsertMeta('name', 'description', description)
    upsertMeta('name', 'application-name', title)
    upsertMeta('name', 'theme-color', '#00C853')

    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:site_name', 'Vexu AI')
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:image', imageUrl ?? iconUrl)

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', imageUrl ?? iconUrl)
  }, [title, description, iconUrl, imageUrl])
}
